import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  FaTachometerAlt, FaUsers, FaMoneyBillWave, FaCog, FaSignOutAlt, FaBan,
  FaUserShield, FaHeart, FaFlag, FaBullhorn, FaChartBar, 
  FaDatabase, FaTimes, FaExclamationTriangle 
} from 'react-icons/fa';

const AdminLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

    // Fetch current admin user from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const adminName = user?.name || 'Admin User';
    const adminRole = user?.role || 'Super Administrator';

    const isActive = (path) => {
        return location.pathname === path ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:bg-gray-800 hover:text-white';
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/admin/login');
        setIsLogoutModalOpen(false);
    };

    const openLogoutModal = () => setIsLogoutModalOpen(true);
    const closeLogoutModal = () => setIsLogoutModalOpen(false);

    return (
      <div className="flex h-screen bg-gray-100">
        {/* Sidebar */}
        <div className="w-64 bg-gray-900 text-white flex flex-col">
          <div className="p-6 flex items-center border-b border-gray-800">
            <FaUserShield className="text-3xl text-blue-500 mr-3" />
            <div>
              <h1 className="text-xl font-bold tracking-wider">ADMIN</h1>
              <p className="text-xs text-gray-500">Control Panel</p>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto custom-scrollbar">
            <p className="px-4 text-xs font-bold text-gray-500 uppercase mt-2 mb-1">
              Overview
            </p>
            <Link
              to="/admin"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin"
              )}`}
            >
              <FaTachometerAlt className="mr-3" /> Dashboard
            </Link>

            <p className="px-4 text-xs font-bold text-gray-500 uppercase mt-4 mb-1">
              Management
            </p>
            <Link
              to="/admin/users"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin/users"
              )}`}
            >
              <FaUsers className="mr-3" /> Users
            </Link>
            <Link
              to="/admin/profiles"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin/profiles"
              )}`}
            >
              <FaUserShield className="mr-3" /> Profile Moderation
            </Link>
            <Link
              to="/admin/matches"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin/matches"
              )}`}
            >
              <FaHeart className="mr-3" /> Matches & Interactions
            </Link>

            <p className="px-4 text-xs font-bold text-gray-500 uppercase mt-4 mb-1">
              Communication & Trust
            </p>
            <Link
              to="/admin/reports"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin/reports"
              )}`}
            >
              <FaFlag className="mr-3" /> User Reports
            </Link>
            <Link
              to="/admin/blocked-users"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin/blocked-users"
              )}`}
            >
              <FaBan className="mr-3" /> Blocked Accounts
            </Link>

            <p className="px-4 text-xs font-bold text-gray-500 uppercase mt-4 mb-1">
              Business
            </p>
            <Link
              to="/admin/revenue"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin/revenue"
              )}`}
            >
              <FaMoneyBillWave className="mr-3" /> Revenue & Plans
            </Link>

            <p className="px-4 text-xs font-bold text-gray-500 uppercase mt-4 mb-1">
              System
            </p>
            <Link
              to="/admin/analytics"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin/analytics"
              )}`}
            >
              <FaChartBar className="mr-3" /> Analytics
            </Link>
            <Link
              to="/admin/master-data"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin/master-data"
              )}`}
            >
              <FaDatabase className="mr-3" /> Master Data
            </Link>
            <Link
              to="/admin/settings"
              className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${isActive(
                "/admin/settings"
              )}`}
            >
              <FaCog className="mr-3" /> Settings
            </Link>
          </nav>

          <div className="p-4 border-t border-gray-800">
            <button
              onClick={openLogoutModal}
              className="flex items-center w-full px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
            >
              <FaSignOutAlt className="mr-3" /> Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <header className="bg-white shadow-sm p-4 flex justify-between items-center sticky top-0 z-10">
            <h2 className="text-xl font-bold text-gray-800">
              {location.pathname === "/admin" && "Dashboard Overview"}
              {location.pathname === "/admin/users" && "User Management"}
              {location.pathname === "/admin/revenue" && "Revenue & Subscriptions"}
              {location.pathname === "/admin/settings" && "System Settings"}
              {/* Add more page titles as needed */}
            </h2>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-bold text-gray-800">{adminName}</p>
                <p className="text-xs text-gray-500">{adminRole}</p>
              </div>
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(adminName)}&background=0D8ABC&color=fff`}
                alt="Admin"
                className="w-10 h-10 rounded-full"
              />
            </div>
          </header>

          <div className="p-6">
            <Outlet />
          </div>
        </div>

        {/* Logout Confirmation Modal */}
        {isLogoutModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 transform transition-all scale-100">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-red-100 rounded-full">
                  <FaExclamationTriangle className="text-4xl text-red-600" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-center text-gray-800 mb-4">
                Confirm Logout
              </h3>
              <p className="text-center text-gray-600 mb-8">
                Are you sure you want to log out of the admin panel?
              </p>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={closeLogoutModal}
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl font-medium hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  className="px-6 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition flex items-center"
                >
                  <FaSignOutAlt className="mr-2" />
                  Logout
                </button>
              </div>

              <button
                onClick={closeLogoutModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
};

export default AdminLayout;